// src/modules/app-views/account/pages/Init/modals/ChangeEmailModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useRef } from 'react';
import { MailOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import authService from 'services/authService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';
import verifyCurrentEmailStep from './Step1';
import enterNewEmailStep from './Step2';
import verifyNewEmailStep from './Step3';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ChangeEmailModal = ({ show, setShow, afterClose }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const storedUser = useZustand('auth', 'user');

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const newEmailDataRef = useRef({ email: '' });

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const currentEmail = storedUser?.email?.value || storedUser?.email || '';

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async ({ form }) => {
    const { put } = useZustand('global', 'useApi', false);

    const newEmail = form?.getFieldValue('newEmail');

    try {
      // Update email in backend
      const result = await put({
        url: mergeUrlParamsQuery({
          url: API.ACCOUNTS.URL,
          params: { _id: storedUser._id },
        }),
        dataSet: {
          email: {
            value: newEmail,
            isVerified: true,
          },
        },
        showSuccessNotification: false,
      });

      if (result) {
        // Refresh user data
        await authService.signInRefresh();

        // Show success notification
        appService.notification({
          flag: 'success',
          title: 'email_changed_success_title',
          description: 'email_changed_success_description',
          duration: 5,
        });

        return true; // Close modal
      }

      return false;
    } catch (err) {
      console.error('[ChangeEmailModal] Error updating email:', err);
      await appService.message({ flag: 'e', text: 'email_change_failed' });
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const steps = useMemo(
    () => [
      // Step 1: Verify current email
      verifyCurrentEmailStep({
        storedUser,
        currentEmail,
      }),

      // Step 2: Enter new email
      enterNewEmailStep({
        newEmailDataRef,
      }),

      // Step 3: Verify new email
      verifyNewEmailStep({
        storedUser,
        newEmailDataRef,
      }),
    ],
    [storedUser, currentEmail]
  );

  // ---------------------------------------------------------------------------
  // 8. EFFECTS II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 9. UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<MailOutlined />}
      title="change_email"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={460}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChangeEmailModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
